import type { MarkSpec, NodeSpec, Schema } from 'prosemirror-model';
import type { EditorState, Plugin, Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Subject } from 'rxjs';

import type { UnionToIntersection } from '@core/common-legacy';

export type ProsemirrorDispatch = (tr: Transaction) => void;
export type ProsemirrorCommand = (state: EditorState, dispatch?: ProsemirrorDispatch) => boolean;

export enum EditorMode {
  View,
  Wysiwyg,
}

export abstract class EditorEvent {
  protected constructor(
    public origin: string,
    public type: string,
  ) {}
}

export type PMDispatch = (tr: Transaction) => void;

export type EditorButtonsGroup = EditorButton[];

export type EditorButtonRunner = (
  state: EditorState,
  dispatch: (tr: Transaction) => void,
  origin: HTMLElement,
  isContextMenu: boolean,
) => void;

export interface EditorButton {
  icon?: string;
  name?: string;
  run: EditorButtonRunner;
  tooltipText?: string;
  tooltipShortcut?: string;
  isActive: (state: EditorState) => boolean;
  labelTranslationKey?: string | ((state: EditorState) => string);
  iconRight?: string;
  iconRightColor?: string;
  isDropdown?: boolean;
  isDisabled?: (state: EditorState) => boolean;
}

/*
 * Default priority is 50
 */
export type PluginWithPriority = { priority: number; plugin: Plugin };

export interface RtPMPlugin {
  /*
   * Here we can publish events that will be available outside plugin
   * It is available in WysiwygComponent.editorEvents
   */
  eventStream$?: Subject<any>;
  /*
   * This hook will be called in WysiwygComponent ngOnDestroy
   */
  destroy?: () => void;
  /*
   * Buttons provided by plugin to be used in editor mode
   */
  buttons?: Record<string, EditorButton>;
  /*
   * Here we can publish methods or services that will be available outside plugin
   * It is available in WysiwygComponent.editorApi$
   */
  api?: Record<string, any>;
  /*
   * This is called before prosemirror init and EditorView doesn't exist yet
   */
  beforeInit?: <S extends Schema>(mode: EditorMode, schema: S) => void;
  /*
   * This is called after prosemirror init, and we can access EditorView
   */
  afterInit?: <EV extends EditorView>(mode: EditorMode, editorView: EV) => void;
  /*
   * This native plugins will be available in editor and view mode
   */
  getNativeViewPlugins?: (mode: EditorMode) => Plugin[];
  /*
   * This native plugins will be only in editor view mode
   */
  getNativeEditorPlugins?: <S extends Schema>(schema: S) => Array<PluginWithPriority | Plugin>;
}

export type RtSchemaType<
  N extends { new (...args: any[]): NodeSpec; nodeName: string } = any,
  M extends { new (...args: any[]): MarkSpec; markName: string } = any,
> = Schema<N['nodeName'], M['markName']>;

export interface IProsemirrorConfig {
  /*
   * Events form plugins will be published on this stream
   */
  eventStream$: Subject<any>;
  /*
   * Schema used in config
   */
  getSchema: () => Schema;
  /*
   * There can be two kinds of plugins one is for view only other is for editor
   * view plugins are also used in editor mode
   * This is mostly done to avoid providing services that will not be used in view mode
   * Recruitee plugins used in render only mode
   */
  getRenderPlugins: () => RtPMPlugin[];
  /*
   * Recruitee plugins used in editor mode
   */
  getEditorPlugins: () => RtPMPlugin[];
  /*
   * This is called during prosemirror creation to listen for events, all events emitted by
   * plugins will be emitted on IProsemirrorConfig.eventStream$
   */
  listenForPluginEvents: (plugins: RtPMPlugin[]) => void;
  /*
   * Custom serialization logic, will be called before WysiwygComponent emits onChange
   * */
  sanitizeJSON?: (node: JsonNode) => JsonNode;
  /*
   * This function is called in editor mode to register buttons in toolbar
   */
  registerToolbarButtons?: (buttons: Record<string, EditorButton>) => void;
}

export type PluginsAPI<T extends RtPMPlugin> = UnionToIntersection<
  T extends Pick<Required<RtPMPlugin>, 'api'> ? T['api'] : never
>;
export type PluginsButtons<T extends RtPMPlugin> = UnionToIntersection<
  T extends Pick<Required<RtPMPlugin>, 'buttons'> ? T['buttons'] : never
>;

type InferArrayType<T> = T extends Array<infer I> ? I : never;
export type ConfigPlugins<
  T extends IProsemirrorConfig,
  P =
    | InferArrayType<ReturnType<T['getEditorPlugins']>>
    | InferArrayType<ReturnType<T['getRenderPlugins']>>,
> = P extends RtPMPlugin ? P : never;

export type PluginsEvents<T extends RtPMPlugin> =
  T extends Required<Pick<RtPMPlugin, 'eventStream$'>>
    ? T['eventStream$'] extends Subject<infer I>
      ? I
      : never
    : never;

export type ConfigEvents<T extends IProsemirrorConfig> = PluginsEvents<ConfigPlugins<T>>;

export interface OverlayPosition {
  top: number;
  left: number;
  right?: number;
  bottom?: number;
}

export interface JsonNode {
  type: string;
  content: JsonNode[];
  attrs: {
    [name: string]: any;
  };
  marks?: JsonMark[];
  text?: string;
}

export interface JsonMark {
  type: string;
  attrs: object[];
}
